import {
  makeStaticStyles,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

export const useStaticStyles = makeStaticStyles(`
  .piq-chat-textarea::before,
  .piq-chat-textarea::after {
    display: none !important;
  }
  .piq-chat-textarea textarea {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: auto !important;
  }
`);

export const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },
  welcomeArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "40px 32px 16px",
    flexShrink: 0,
  },
  welcomeBadge: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.xs,
  },
  welcomeBadgeIcon: {
    color: designTokens.accent[500],
    width: "16px",
    height: "16px",
    backgroundColor: designTokens.neutral[100],
    borderRadius: designTokens.borderRadius.sm,
    padding: designTokens.spacing[1],
  },
  welcomeBadgeIconClaude: {
    color: "#16A34A",
    backgroundColor: "#ECFDF5",
  },
  welcomeBadgeIconOpenai: {
    color: "#0078D4",
    backgroundColor: "#EFF6FF",
  },
  welcomeTitle: {
    fontSize: designTokens.fontSize["2xl"],
    fontWeight: "700",
    color: designTokens.neutral[900],
    textAlign: "center",
  },
  welcomeSubtitle: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[500],
    textAlign: "center",
    maxWidth: "480px",
    lineHeight: "1.5",
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
  },
  messagesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "720px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  messageRow: {
    display: "flex",
    flexDirection: "column",
    padding: "12px",
    borderRadius: designTokens.borderRadius.lg,
  },
  assistantMessage: {
    backgroundColor: designTokens.neutral[50],
  },
  userMessage: {
    display: "flex",
    justifyContent: "end",
    alignItems: "end",
    width: "100%",
  },
  messageContent: {
    marginTop: designTokens.spacing[2],
    paddingLeft: "48px",
    paddingRight: "16px",
    wordBreak: "break-word",
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[900],
    "& code": {
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      maxWidth: "100%",
      display: "block",
      backgroundColor: designTokens.neutral.white,
      padding: designTokens.spacing[2],
      borderRadius: designTokens.borderRadius.sm,
      overflowX: "auto",
    },
    "& pre": {
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      maxWidth: "100%",
      backgroundColor: designTokens.neutral.white,
      padding: designTokens.spacing[2],
      borderRadius: designTokens.borderRadius.sm,
      overflowX: "auto",
    },
  },
  skeletonContent: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  messageContentWrapper: {
    marginBottom: "-30px",
  },
  messageActions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[1],
    marginTop: designTokens.spacing[1],
  },
  messageActionButton: {
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    ...shorthands.padding(designTokens.spacing[1]),
    paddingLeft: 0,
    borderRadius: designTokens.borderRadius.sm,
    color: designTokens.neutral[500],
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      color: designTokens.neutral[700],
      backgroundColor: designTokens.neutral[100],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
  messageBarArea: {
    padding: "8px 16px 16px",
    maxWidth: "720px",
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
    boxSizing: "border-box",
  },
  inputCard: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: designTokens.neutral.white,
    border: `2px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius["2xl"],
    boxShadow: designTokens.shadow.md,
    overflow: "hidden",
    ":focus-within": {
      border: `2px solid ${designTokens.accent[500]}`,
    },
  },
  textArea: {
    width: "100%",
    ...shorthands.border("none"),
    outline: "none",
    backgroundColor: designTokens.neutral.white,
    resize: "none",
    fontSize: designTokens.fontSize.sm,
    minHeight: "40px",
    boxSizing: "border-box",
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[3],
    ":hover": {
      ...shorthands.border("none"),
    },
    ":focus": {
      ...shorthands.border("none"),
    },
    ":focus-within": {
      ...shorthands.border("none"),
    },
    ":active": {
      ...shorthands.border("none"),
    },
  },
  innerTextarea: {
    lineHeight: designTokens.spacing[5],
  },
  innerTextareaBusy: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
  },
  toolbarSpacer: {
    flex: 1,
  },
  sendButton: {
    width: "30px",
    height: "30px",
    minWidth: "30px",
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    backgroundColor: designTokens.accent[500],
    ...shorthands.border("none"),
    color: designTokens.neutral.white,
    ...shorthands.padding("0"),
    ...shorthands.margin(designTokens.spacing[1]),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    ":hover": {
      backgroundColor: designTokens.accent[600],
      transform: "scale(1.05)",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  sendButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  stopButton: {
    width: "30px",
    height: "30px",
    minWidth: "30px",
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    backgroundColor: designTokens.neutral[200],
    ...shorthands.border("none"),
    color: designTokens.neutral[600],
    ...shorthands.padding("0"),
    ...shorthands.margin(designTokens.spacing[1]),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: `all ${designTokens.transition.base}`,
    ":hover": {
      backgroundColor: designTokens.neutral[300],
      transform: "scale(1.05)",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  toggleButton: {
    ...shorthands.borderRadius(designTokens.borderRadius.lg),
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.normal,
    minHeight: "auto",
    ...shorthands.padding(designTokens.spacing[1], designTokens.spacing[2]),
  },
  toggleButtonActive: {
    backgroundColor: designTokens.accent[50],
    color: designTokens.accent[700],
    ...shorthands.borderColor(designTokens.accent[500]),
  },
  // Fixed box shared by attachButton and promptButtonWrapper so both controls
  // render at the same reduced size as AIModelSettings' compactButton/nhToggleCompact (30px).
  attachButton: {
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    ...shorthands.borderRadius(designTokens.borderRadius.lg),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    ...shorthands.padding("0"),
    boxSizing: "border-box",
  },
  // Overrides ghostButton's baked-in padding/min sizing so attachButton's
  // outer div is the single padding box (no stacked double-padding).
  contentSourceButtonReset: {
    ...shorthands.padding("0"),
    minWidth: "auto",
    minHeight: "auto",
    width: "100%",
    height: "100%",
  },
  promptButtonWrapper: {
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    ...shorthands.borderRadius(designTokens.borderRadius.lg),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    boxSizing: "border-box",
  },
  contentSourceIcon: {
    color: designTokens.neutral[600],
    ":hover": {
      color: designTokens.accent[500],
    },
  },
  contentSourceButtonWrapper: {
    position: "relative",
    display: "inline-flex",
  },
  contentSourceBadge: {
    position: "absolute",
    top: "-4px",
    left: "-6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "10px",
    height: "18px",
    backgroundColor: designTokens.accent[500],
    color: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    paddingLeft: designTokens.spacing[1],
    paddingRight: designTokens.spacing[1],
    pointerEvents: "none",
  },
  chatBreadcrumbBar: {
    flexShrink: 0,
    height: "64px",
    borderBottom: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    display: "flex",
    alignItems: "center",
    padding: "0 28px",
    gap: "12px",
  },
  chatBackButton: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    border: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    color: projectIqTokens.muted,
    height: "36px",
    padding: "0 13px",
    borderRadius: "9px",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: projectIqTokens.fontFamily,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.violet}`,
      outlineOffset: "2px",
    },
  },
  chatBackButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  chatBreadcrumbSeparator: {
    color: projectIqTokens.faint,
  },
  chatBreadcrumbTitle: {
    fontSize: "14.5px",
    fontWeight: 700,
    color: projectIqTokens.ink,
  },
});
